import { z } from 'zod';
import type { components } from './schema.js';
import { montarCaminho, requisitar } from './client.js';
import { codigoDoErro, ErroApi, mensagemPorCodigo } from './erros.js';
import { analisar } from './validacao.js';

/**
 * Leitura por imagem no inventário: o matching de QR do fallback
 * (qr-code.md §6.1 e §9.7).
 *
 * A flag nasce desligada. Desligada, o coletor não mostra "Não consegui ler",
 * não oferece a foto nem "Identificar no servidor" — a leitura normal segue
 * igual.
 *
 * `GET` para Administrador e Supervisor, `PUT` só para o Administrador, e o
 * `PUT` leva a configuração INTEIRA, sempre: não há atualização parcial.
 */

const CAMINHO = montarCaminho('/matching-qr/configuracao');

/**
 * O 400 do `PUT` não traz `codigo` — só `campos` com o caminho e o código do
 * zod. O painel dá a ele um código próprio para a tela decidir por código e
 * mostrar a frase de faixa, não "Corpo da requisição inválido".
 */
export const PARAMETRO_FORA_DA_FAIXA = 'PARAMETRO_DE_LEITURA_FORA_DA_FAIXA';

export const MODOS_DE_CANDIDATOS = ['SALDO_FILIAL', 'CATALOGO'] as const;

export type ModoDeCandidatos = components['schemas']['ModoCandidatosMatching'];

export const parametrosDeLeituraSchema = z.object({
  fallbackTimeoutMs: z.number(),
  warpSize: z.number(),
  minPxPorModulo: z.number(),
  scoreMinimo: z.number(),
  scoreFaixaMedia: z.number(),
  scoreAceite: z.number(),
  margemAceite: z.number(),
  maxCandidatosConfirmacao: z.number(),
}) satisfies z.ZodType<components['schemas']['ParametrosMatchingResponse']>;

export type ParametrosDeLeitura = z.infer<typeof parametrosDeLeituraSchema>;

export const configuracaoDeLeituraSchema = z.object({
  habilitado: z.boolean(),
  modo: z.enum(MODOS_DE_CANDIDATOS),
  parametros: parametrosDeLeituraSchema,
}) satisfies z.ZodType<components['schemas']['ConfiguracaoMatchingResponse']>;

export type ConfiguracaoDeLeituraPorImagem = z.infer<typeof configuracaoDeLeituraSchema>;

/** Os valores iniciais da §9.7 — os mesmos que a API usa sem nada gravado. */
export const PARAMETROS_PADRAO: ParametrosDeLeitura = Object.freeze({
  fallbackTimeoutMs: 2500,
  warpSize: 640,
  minPxPorModulo: 2,
  scoreMinimo: 0.45,
  scoreFaixaMedia: 0.55,
  scoreAceite: 0.65,
  margemAceite: 0.15,
  maxCandidatosConfirmacao: 5,
});

export async function obterLeituraPorImagem(): Promise<ConfiguracaoDeLeituraPorImagem> {
  return analisar(configuracaoDeLeituraSchema, await requisitar(CAMINHO), CAMINHO);
}

/**
 * Grava a configuração inteira.
 *
 * @throws {ErroApi} `PARAMETRO_DE_LEITURA_FORA_DA_FAIXA` (400) quando algum
 * parâmetro sai da faixa da §9.7 ou os scores saem de ordem.
 */
export async function configurarLeituraPorImagem(
  configuracao: ConfiguracaoDeLeituraPorImagem,
): Promise<ConfiguracaoDeLeituraPorImagem> {
  const corpo: components['schemas']['ConfigurarMatchingRequest'] = configuracao;

  try {
    const resposta = await requisitar(CAMINHO, { metodo: 'PUT', corpo });
    return analisar(configuracaoDeLeituraSchema, resposta, CAMINHO);
  } catch (falha: unknown) {
    if (codigoDoErro(falha) === 'REQUISICAO_INVALIDA') {
      throw new ErroApi(
        400,
        PARAMETRO_FORA_DA_FAIXA,
        mensagemPorCodigo(PARAMETRO_FORA_DA_FAIXA),
        CAMINHO,
      );
    }
    throw falha;
  }
}
